import React, { useState } from 'react';
import styles from './customChat.module.scss';
import classnames from 'classnames';
import { SearchEntity } from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import { useQuery } from 'react-query';
import { Spinner } from 'react-bootstrap';
import TimeAgo from 'react-timeago';
import { formatTalkJsTime } from 'v2/utils/dateUtils';
import Avatar from 'v2/Avatar/Avatar';
import TabNosReults from 'v2/common/TabNosReults/TabNosReults';
import avatarPerson from '../images/avatarPerson.png';
import avatarPersonNoProfilePic from '../images/avatarPersonNoProfilePic.png';
import {
  ReadOnlyConversation,
  userType,
  participant,
} from 'v2/common/LinkEntity/useAddSearchDRMLinkActivity';

interface chatQueryResponse {
  results: ReadOnlyConversation[];
  count: number;
  next: string | null;
  previous: string | null;
}

type headerInfo = {
  [key in userType]: {
    full_name?: string;
    avatar?: string;
    full_job_title?: string;
    first_letter_name?: string;
    color?: string;
  };
};

export interface CustomChatProps {
  search: SearchEntity;
}

const CustomChat = ({ search }: CustomChatProps) => {
  const [currentConversation, setCurrentConversation] =
    useState<ReadOnlyConversation | null>(null);
  const [headerData, setHeaderData] = useState<headerInfo | null>(null);
  const [readOnlyFrame, setReadOnlyFrame] = useState<string | null>('');
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  const { data: conversations, isFetchedAfterMount: isConversationsSuccess } =
    useQuery(
      ['Get conversations by search_id'],
      async () => {
        return getConversations(search.id);
      },
      {
        staleTime: Infinity,
        refetchOnMount: 'always',
        onSuccess(data) {
          if (data && data.length) {
            selectedConversation(data[0]);
          }
        },
      }
    );

  if (isConversationsSuccess && conversations === null) {
    return (
      <div
        className={classnames(
          'container-xl',
          'fix-bg-images',
          'h-100',
          styles.noConversationsTab
        )}
      >
        <TabNosReults
          message={`There are no conversations yet!`}
          showTopImage={true}
        />
      </div>
    );
  }

  const getConversations = async (search_id: number) => {
    try {
      const conversationsFetched = await restFactory.get<chatQueryResponse>(
        `/dj/api/v1/conversations/get_conversations_from_search_id/`,
        { search_id, size: 100, page: 1 }
      );
      let conversationsCustomized: ReadOnlyConversation[] | null = null;

      if (
        conversationsFetched.results &&
        conversationsFetched.results.length > 0
      ) {
        conversationsCustomized = conversationsFetched.results.map(
          (conversation) => {
            const localTime = new Date(
              conversation.last_message_timestamp.replace(/\.\d+/, ' UTC')
            );
            return {
              ...conversation,
              color: generateDarkColorHex(),
              selected: false,
              last_message_timestamp: localTime.toLocaleString(),
            };
          }
        );
      }
      return conversationsCustomized;
    } catch (error) {
      return null;
    }
  };

  const getIframe = async (conversation_id: string) => {
    type iframeType = { ['i-frame']: string };
    try {
      const iFrame = await restFactory.get<iframeType>(
        `/dj/api/v1/conversations/${conversation_id}/get_read_only_chat/`
      );
      setReadOnlyFrame(iFrame['i-frame']);
    } catch (error) {
      setReadOnlyFrame('');
      setIsIframeLoaded(true);
    }
  };

  const selectedConversation = (conversation: ReadOnlyConversation) => {
    buildHeaderData(conversation);
    setCurrentConversation(conversation);
    setReadOnlyFrame(null);
    setIsIframeLoaded(false);
    getIframe(conversation.conversation_id);
  };

  const buildHeaderData = (conversation: ReadOnlyConversation) => {
    const participants = conversation.participants;
    const buildHeaderInfo: headerInfo = { internal: {}, external: {} };
    participants.forEach((participant) => {
      const participantData = getParticipantData(participant);
      buildHeaderInfo[participant.user_type] = {
        full_name: participantData.full_name,
        full_job_title: participantData.full_job_title,
        avatar: participantData.avatar,
        first_letter_name: participantData.first_letter_name,
        color:
          participant.user_type === 'internal'
            ? conversation.color
            : generateDarkColorHex(),
      };
    });
    setHeaderData(buildHeaderInfo);
  };

  const getParticipantData = (participant: participant) => {
    let full_name = 'Name hidden';
    if (participant.first_name && participant.last_name) {
      full_name = `${participant.first_name} ${participant.last_name}`;
    }
    let full_job_title = 'Title hidden';
    if (participant.job_title && participant.organization_name) {
      full_job_title = `${participant.job_title}, ${participant.organization_name}`;
    } else if (participant.job_title) {
      full_job_title = participant.job_title;
    } else if (participant.organization_name) {
      full_job_title = participant.organization_name;
    }
    const avatar =
      full_name === 'Name hidden'
        ? avatarPerson
        : participant.avatar_url
        ? participant.avatar_url
        : avatarPersonNoProfilePic;
    const first_letter_name = full_name[0];
    return { full_name, full_job_title, avatar, first_letter_name };
  };

  function generateDarkColorHex() {
    let color = '#';
    for (let i = 0; i < 3; i++)
      color += (
        '0' + Math.floor((Math.random() * Math.pow(16, 2)) / 2).toString(16)
      ).slice(-2);
    return color;
  }

  const getNoIFrameSelected = (message: string) => {
    return (
      <div className={styles.chatContainer}>
        <div
          className={classnames(styles.chat, {
            [styles.noConversationSelected]: readOnlyFrame === '',
          })}
        >
          <span className={styles.temporal_text}>{message}</span>
        </div>
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div className={classnames(styles.header)}>
        <div className={styles.user}>
          <Avatar
            initials={headerData?.internal.first_letter_name}
            url={headerData?.internal.avatar}
            backgroundColor={headerData?.internal.color}
            className="mr-2"
          />
          <div className={classnames(styles.inner)}>
            <div className={styles.inbox_box}>
              <div className={classnames(styles.name, styles.wrapText)}>
                <span>{headerData?.internal.full_name}</span>
              </div>
            </div>
            <div className={styles.body}>
              <div className={styles.wrapText}>
                <span> {headerData?.internal.full_job_title}</span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.user}>
          <div className={classnames(styles.inner)}>
            <div
              className={classnames(styles.inbox_box, {
                [styles.isExternalUser]: true,
              })}
            >
              <div className={classnames(styles.name, styles.wrapText)}>
                <span>{headerData?.external.full_name}</span>
              </div>
            </div>
            <div className={classnames(styles.body)}>
              <div className={styles.wrapText}>
                <span> {headerData?.external.full_job_title}</span>
              </div>
            </div>
          </div>
          <Avatar
            initials={headerData?.external.first_letter_name}
            url={headerData?.external.avatar}
            backgroundColor={headerData?.external.color}
            className="ml-2"
          />
        </div>
      </div>
    );
  };

  const renderInbox = () => {
    return (
      <div className={styles.conversations}>
        <nav
          aria-label="conversationList"
          role="tablist"
          className={styles.nav}
        >
          <ul className={styles.ul}>
            {conversations?.map((conversation, index) => {
              const inboxParticipant = conversation.participants.find(
                (participant) => participant.user_type === 'external'
              );
              const participantData = inboxParticipant
                ? getParticipantData(inboxParticipant)
                : null;
              return (
                <li
                  className={classnames(styles.li)}
                  key={index}
                  onClick={() => selectedConversation(conversation)}
                >
                  <div
                    className={classnames(
                      {
                        [styles.conversationSelected]:
                          currentConversation &&
                          currentConversation.conversation_id ===
                            conversation.conversation_id,
                      },
                      styles.conversation
                    )}
                  >
                    <Avatar
                      initials={participantData?.first_letter_name}
                      url={participantData?.avatar}
                      backgroundColor={conversation.color}
                      className="mr-2"
                    />
                    <div className={classnames(styles.inner)}>
                      <div className={styles.inbox_box}>
                        <div
                          className={classnames(styles.name, styles.wrapText)}
                        >
                          <span>
                            {participantData && participantData.full_name}
                          </span>
                        </div>
                        <span>
                          <TimeAgo
                            date={conversation.last_message_timestamp}
                            className={classnames(styles.lastUpdate)}
                            live={false}
                            formatter={(value: number, unit: string) =>
                              formatTalkJsTime(
                                value,
                                unit,
                                conversation.last_message_timestamp
                              )
                            }
                          />
                        </span>
                      </div>
                      <div className={styles.body}>
                        <div className={styles.wrapText}>
                          <span>
                            {participantData && participantData.full_job_title}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    );
  };

  const renderIframeRetrieved = () => {
    return (
      <div
        className={classnames(styles.chatContainer, {
          [styles.loadingContent]: !isIframeLoaded,
        })}
      >
        {isIframeLoaded && renderHeader()}
        {readOnlyFrame && (
          <iframe
            title="iframe"
            src={readOnlyFrame}
            className={classnames(styles.chat)}
            onLoad={function () {
              setIsIframeLoaded(true);
            }}
            data-cy="talkjs-custom-chat-iframe"
          ></iframe>
        )}
      </div>
    );
  };

  const renderChatBox = () => {
    return (
      <>
        {currentConversation ? (
          <>
            {!isIframeLoaded && (
              <div className={classnames(styles.loading)}>
                <Spinner animation="border" />
              </div>
            )}
            {readOnlyFrame !== ''
              ? renderIframeRetrieved()
              : getNoIFrameSelected('Conversation unavailable')}
          </>
        ) : (
          getNoIFrameSelected('Choose a conversation')
        )}
      </>
    );
  };

  return (
    <>
      {!isConversationsSuccess ? (
        <div className="loadingIndicatorContainer">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className={styles.generalContainer}>
          <div className={styles.container}>
            {renderInbox()}
            {renderChatBox()}
          </div>
        </div>
      )}
    </>
  );
};

export default CustomChat;
